import React, { useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  InputNumber,
  Row,
  Select,
  Space,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
} from 'antd';
import { useProducts } from '@shared/api/hooks/useProducts';
import {
  FanTierDto,
  useSimulateDiscounts,
} from '@shared/api/hooks/useDiscountRules';
import { SKIP_REASON_IT } from './ruleSummary';

const { Text } = Typography;
const eur = (cents: number): string => `${(cents / 100).toFixed(2)} EUR`;

interface Row {
  productId: string;
  quantity: number;
}

/**
 * Simulatore: gira la STESSA valutazione del checkout su un carrello di prova.
 * Mostra anche cosa NON si applica e perche' — senza il "perche' no" l'admin
 * non ha modo di capire cosa ha sbagliato nella regola.
 */
export function DiscountSimulator({ tiers }: { tiers: FanTierDto[] }): React.ReactElement {
  const { data: productsPage } = useProducts({ limit: 200 });
  const products = productsPage?.data ?? [];
  const simulate = useSimulateDiscounts();
  const [rows, setRows] = useState<Row[]>([]);
  const [tierId, setTierId] = useState<string | undefined>();
  const [firstPurchase, setFirstPurchase] = useState(false);
  const [shippingCents, setShippingCents] = useState(0);

  const result = simulate.data;

  const run = (): void => {
    simulate.mutate({
      items: rows.filter((r) => r.productId && r.quantity > 0),
      tierId,
      firstPurchase,
      fulfillmentMethod: shippingCents > 0 ? 'SHIPPING' : 'IN_STORE_PICKUP',
      shippingCents,
    });
  };

  return (
    <Row gutter={16}>
      <Col span={10}>
        <Card size="small" title="Carrello di prova">
          <Space direction="vertical" style={{ width: '100%' }}>
            {rows.map((r, i) => (
              <Space key={i} style={{ width: '100%' }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  style={{ width: 240 }}
                  placeholder="Prodotto"
                  value={r.productId || undefined}
                  onChange={(v) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, productId: v } : x)))
                  }
                  options={products.map((p) => ({
                    value: p.id,
                    label: `${p.name.it ?? p.name.en ?? p.sku} — ${Number(p.price).toFixed(2)} EUR`,
                  }))}
                />
                <InputNumber
                  min={1}
                  max={99}
                  value={r.quantity}
                  onChange={(v) =>
                    setRows(rows.map((x, j) => (j === i ? { ...x, quantity: v ?? 1 } : x)))
                  }
                />
                <Button danger type="text" onClick={() => setRows(rows.filter((_, j) => j !== i))}>
                  Rimuovi
                </Button>
              </Space>
            ))}
            <Button
              type="dashed"
              block
              onClick={() => setRows([...rows, { productId: '', quantity: 1 }])}
            >
              + Aggiungi articolo
            </Button>

            <Select
              allowClear
              style={{ width: '100%' }}
              placeholder="Tier del tifoso (nessuno)"
              value={tierId}
              onChange={setTierId}
              options={tiers.map((t) => ({ value: t.id, label: `${t.key} (rank ${t.rank})` }))}
            />
            <Space>
              <Switch checked={firstPurchase} onChange={setFirstPurchase} />
              <Text>Primo acquisto</Text>
            </Space>
            <Space>
              <Text>Spedizione (cent):</Text>
              <InputNumber min={0} value={shippingCents} onChange={(v) => setShippingCents(v ?? 0)} />
            </Space>
            <Button
              type="primary"
              block
              loading={simulate.isPending}
              disabled={rows.length === 0}
              onClick={run}
            >
              Simula
            </Button>
            <Text type="secondary" style={{ fontSize: 12 }}>
              I prezzi vengono letti dal catalogo, come nel checkout reale. Nessuna scrittura:
              non consuma utilizzi.
            </Text>
          </Space>
        </Card>
      </Col>

      <Col span={14}>
        {simulate.isError && (
          <Alert type="error" showIcon message="Simulazione fallita" description={String(simulate.error)} />
        )}
        {!result && !simulate.isError && <Empty description="Nessuna simulazione eseguita" />}
        {result && (
          <Space direction="vertical" style={{ width: '100%' }}>
            <Card size="small">
              <Row gutter={16}>
                <Col span={6}>
                  <Statistic title="Subtotale" value={eur(result.subtotalCents)} />
                </Col>
                <Col span={6}>
                  <Statistic title="Sconto articoli" value={eur(result.itemsDiscountCents)} />
                </Col>
                <Col span={6}>
                  <Statistic title="Sconto spedizione" value={eur(result.shippingDiscountCents)} />
                </Col>
                <Col span={6}>
                  <Statistic title="Totale" value={eur(result.totalCents)} />
                </Col>
              </Row>
            </Card>

            <Card size="small" title={`Applicate (${result.applied.length})`}>
              {result.applied.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuno sconto applicato" />
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="ruleId"
                  dataSource={result.applied}
                  columns={[
                    { title: 'Regola', dataIndex: 'ruleName' },
                    {
                      title: 'Su',
                      dataIndex: 'appliedTo',
                      render: (v: string) => (
                        <Tag>{v === 'SHIPPING' ? 'spedizione' : 'articoli'}</Tag>
                      ),
                    },
                    {
                      title: 'Sconto',
                      dataIndex: 'amountCents',
                      align: 'right' as const,
                      render: (v: number) => eur(v),
                    },
                  ]}
                />
              )}
            </Card>

            <Card size="small" title={`Non applicate (${result.skipped.length})`}>
              {result.skipped.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna regola scartata" />
              ) : (
                <Table
                  size="small"
                  pagination={false}
                  rowKey="ruleId"
                  dataSource={result.skipped}
                  columns={[
                    { title: 'Regola', dataIndex: 'ruleName' },
                    {
                      title: 'Perche no',
                      dataIndex: 'reason',
                      render: (r: keyof typeof SKIP_REASON_IT) => (
                        <Text type="secondary">{SKIP_REASON_IT[r] ?? r}</Text>
                      ),
                    },
                  ]}
                />
              )}
            </Card>
          </Space>
        )}
      </Col>
    </Row>
  );
}
